import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Badge, Box, Card, Code, Flex, Heading, Table, Text } from "@radix-ui/themes";
import { Bell } from "lucide-react";
import { anyService } from "@/services/breeze/AnyService";
import {
  DeviceSourceConfig,
  tenantPushService,
} from "@/services/breeze/TenantPushService";

interface Props {
  ownerEntityName?: string;
  ownerEntityId: number;
  title?: string;
  emptyHint?: string;
}

type RowShape = {
  rowKey: string;
  provider: "fcm" | "web_push" | "apns";
  source: "tenant_devices" | "entity_field";
  token_preview: string;
  last_seen_at: string;
  unregistered_at: string | null;
};

function tokenPreview(token: string | null | undefined): string {
  if (!token) return "—";
  if (token.length <= 16) return token;
  return `${token.slice(0, 8)}…${token.slice(-4)}`;
}

function prettyProvider(p: string): string {
  switch (p) {
    case "fcm":
      return "FCM";
    case "web_push":
      return "Web Push";
    case "apns":
      return "APNs";
    default:
      return p;
  }
}

export default function RegisteredPushDevicesCard({
  ownerEntityName = "anythink_users",
  ownerEntityId,
  title = "Registered push devices",
  emptyHint = "No push devices registered for this owner.",
}: Props) {
  const { data: registry } = useQuery({
    queryKey: ["push-devices", "for-owner", ownerEntityName, ownerEntityId],
    queryFn: () => tenantPushService.getDevices(false),
  });
  const { data: credentials } = useQuery({
    queryKey: ["push-credentials"],
    queryFn: () => tenantPushService.getCredentials(),
  });

  const entityFieldSources = useMemo(
    () =>
      (credentials?.providers ?? [])
        .map((p) => {
          const ds = p.metadata?.device_source as DeviceSourceConfig | undefined;
          if (!ds || ds.kind !== "entity_field" || !ds.entity || !ds.token_field) return null;
          return { provider: p.provider, source: ds };
        })
        .filter(Boolean) as Array<{
          provider: "fcm" | "web_push" | "apns";
          source: DeviceSourceConfig & { entity: string; token_field: string };
        }>,
    [credentials],
  );

  const entityFieldQueries = useQueries({
    queries: entityFieldSources.map(({ provider, source }) => {
      const filter = buildOwnerFilter(source, ownerEntityName, ownerEntityId);
      return {
        queryKey: ["push-devices", "entity-field", provider, source.entity, ownerEntityName, ownerEntityId],
        queryFn: async () => {
          if (filter === null) return { provider, source, items: [] as Array<Record<string, unknown>> };
          const fields = [source.token_field, "updated_at"].filter(Boolean) as string[];
          const result = await anyService.getAll<Record<string, unknown>>(source.entity, fields, filter, 1, 100);
          return { provider, source, items: result.items };
        },
        enabled: filter !== null,
      };
    }),
  });

  const rows = useMemo<RowShape[]>(() => {
    const fromRegistry: RowShape[] = (registry?.devices ?? [])
      .filter((d) => {
        if (ownerEntityName === "anythink_users") {
          return d.user_id === ownerEntityId
            || (d.owner_entity_name === "anythink_users" && d.owner_entity_id === ownerEntityId);
        }
        return d.owner_entity_name === ownerEntityName && d.owner_entity_id === ownerEntityId;
      })
      .map((d) => ({
        rowKey: `td-${d.id}`,
        provider: d.provider,
        source: "tenant_devices" as const,
        token_preview: d.token_preview,
        last_seen_at: d.last_seen_at,
        unregistered_at: d.unregistered_at,
      }));

    const fromEntities: RowShape[] = entityFieldQueries.flatMap((q) => {
      if (!q.data) return [] as RowShape[];
      const { provider, source, items } = q.data;
      return items
        .map((row) => {
          const rawToken = row[source.token_field] as string | null | undefined;
          if (!rawToken) return null;
          const updated = (row.updated_at as string | undefined) ?? new Date(0).toISOString();
          const rowId = (row.id as number | undefined) ?? 0;
          return {
            rowKey: `ef-${provider}-${source.entity}-${rowId}`,
            provider,
            source: "entity_field" as const,
            token_preview: tokenPreview(rawToken),
            last_seen_at: updated,
            unregistered_at: null,
          } as RowShape;
        })
        .filter(Boolean) as RowShape[];
    });

    return [...fromRegistry, ...fromEntities].sort(
      (a, b) => new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime(),
    );
  }, [registry, ownerEntityName, ownerEntityId, entityFieldQueries.map((q) => q.dataUpdatedAt).join(",")]);

  return (
    <Card>
      <Box p="2">
        <Flex align="center" gap="2" mb="3">
          <Bell size={16} />
          <Heading size="4" weight="medium" mt="0">{title}</Heading>
        </Flex>
        {rows.length === 0 ? (
          <Text size="2" color="gray">{emptyHint}</Text>
        ) : (
          <Table.Root variant="surface" size="1">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Provider</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Source</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Token</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Last seen</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((r) => (
                <Table.Row key={r.rowKey}>
                  <Table.Cell>
                    <Badge variant="soft" size="1">{prettyProvider(r.provider)}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      size="1"
                      variant="soft"
                      color={r.source === "entity_field" ? "purple" : "blue"}
                    >
                      {r.source === "entity_field" ? "Entity field" : "Registry"}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Code size="1" variant="ghost">{r.token_preview}</Code>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1">{new Date(r.last_seen_at).toLocaleString()}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    {r.unregistered_at
                      ? <Badge color="gray" variant="soft" size="1">Unregistered</Badge>
                      : <Badge color="green" variant="soft" size="1">Live</Badge>}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Box>
    </Card>
  );
}

function buildOwnerFilter(
  source: DeviceSourceConfig & { entity: string; token_field: string },
  ownerEntityName: string,
  ownerEntityId: number,
): string | null {
  if (ownerEntityName === "anythink_users") {
    if (!source.owner_user_field) return null;
    return `${source.owner_user_field}=${ownerEntityId}`;
  }
  if (source.owner_self && ownerEntityName === source.entity) {
    return `id=${ownerEntityId}`;
  }
  if (source.owner_entity_field) {
    return `${source.owner_entity_field}=${ownerEntityId}`;
  }
  return null;
}
